import React, { useEffect, useRef, useState } from "react";

// pdf.js is loaded on demand so only the public offer pages pay its bundle
// cost; the worker stays self-hosted (no CDN) as a webpack-emitted asset.
async function loadPdfjs() {
  const pdfjsLib = await import("pdfjs-dist");
  pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    "pdfjs-dist/build/pdf.worker.min.mjs",
    import.meta.url,
  ).toString();
  return pdfjsLib;
}

/**
 * Renders the actual generated offer PDF into a scrollable in-page viewer
 * (spec addendum AC16/AC17). Reports scroll progress so action buttons can be
 * gated on having read to the end; unlocks immediately when the document fits
 * without scrolling, or when the viewer fails to load (a download link is the
 * fallback there — never trap the reader behind a broken canvas).
 */
export function OfferPdfViewer({ url, onReadToEnd, height = 480 }: {
  url: string;
  onReadToEnd: () => void;
  height?: number;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const reportedRef = useRef(false);

  function reportReadToEnd() {
    if (!reportedRef.current) {
      reportedRef.current = true;
      onReadToEnd();
    }
  }

  useEffect(() => {
    let cancelled = false;
    const container = containerRef.current;
    if (!container) return;

    (async () => {
      try {
        const pdfjsLib = await loadPdfjs();
        const doc = await pdfjsLib.getDocument({ url }).promise;
        if (cancelled) return;
        container.innerHTML = "";
        const width = container.clientWidth - 2; // borders
        const ratio = window.devicePixelRatio || 1;
        for (let i = 1; i <= doc.numPages; i++) {
          const page = await doc.getPage(i);
          if (cancelled) return;
          const base = page.getViewport({ scale: 1 });
          const scale = width / base.width;
          const viewport = page.getViewport({ scale });
          const canvas = document.createElement("canvas");
          canvas.width = viewport.width * ratio;
          canvas.height = viewport.height * ratio;
          canvas.style.width = `${viewport.width}px`;
          canvas.style.height = `${viewport.height}px`;
          canvas.style.display = "block";
          canvas.style.borderBottom = "1px solid #e2e8f0";
          const ctx = canvas.getContext("2d")!;
          ctx.scale(ratio, ratio);
          await page.render({ canvasContext: ctx, viewport, canvas }).promise;
          if (cancelled) return;
          container.appendChild(canvas);
        }
        setState("ready");
        // Fits without scrolling → nothing to gate on.
        if (container.scrollHeight <= container.clientHeight + 8) reportReadToEnd();
      } catch {
        if (!cancelled) {
          setState("error");
          reportReadToEnd(); // never block actions behind a broken viewer
        }
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  function onScroll() {
    const el = containerRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) reportReadToEnd();
  }

  return (
    <div>
      <div style={{ position: "relative" }}>
        {/* This div is owned entirely by the imperative pdf.js code above
            (innerHTML/appendChild) — React must never render children into
            it, or its own reconciler and pdf.js's direct DOM writes fight
            over the same nodes ("removeChild: node is not a child of this
            node") the moment a render lands right after pdf.js has already
            replaced what React expected to find there. The loading text is
            a sibling overlay instead, fully React-owned on its own node. */}
        <div
          ref={containerRef}
          onScroll={onScroll}
          data-testid="offer-pdf-viewer"
          style={{
            height, overflowY: "auto", background: "#f1f5f9",
            border: "1px solid #e2e8f0", borderRadius: 10,
          }}
        />
        {state === "loading" && (
          <div style={{
            position: "absolute", inset: 0, display: "flex",
            alignItems: "center", justifyContent: "center",
            color: "#64748b", fontSize: 13, pointerEvents: "none",
          }}>
            Loading document…
          </div>
        )}
      </div>
      {state === "error" && (
        <p style={{ margin: "8px 0 0", fontSize: 12, color: "#b45309", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "8px 12px" }}>
          The document preview couldn't be displayed in your browser — use the{" "}
          <a href={url} target="_blank" rel="noreferrer" style={{ color: "#1e3a8a", fontWeight: 600 }}>Download PDF</a>{" "}
          link to read the full offer before responding.
        </p>
      )}
    </div>
  );
}
